import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  useEvent,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
} from '../../../../common'
import { type RepeatingTransaction, Frequency, RepeatingType } from '../../../../core'

import { RecurringRow } from './RecurringRow'
import { AddRecurringTransaction } from './AddRecurringTransaction'
import { RecurringSettingsZeroState } from './RecurringSettingsZeroState'

interface ManageIncomeProps {
  forOverview?: boolean
}

export const ManageIncome = observer(({ forOverview = false }: ManageIncomeProps) => {
  const { recurring: copy } = useGlobalCopyStore()
  const { detailedRepeatingTransactions } = useRecurringTransactionsStore()
  const { onEvent } = useEvent()

  const onClick = (repeatingTransaction: RepeatingTransaction) => {
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_INCOME, {
      repeating_transaction_guid: repeatingTransaction.guid,
    })
  }

  const onAdd = () => {
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_ADD_INCOME)
  }

  const filteredTransactions = detailedRepeatingTransactions.filter(
    (rt) =>
      rt.repeating_transaction_type === RepeatingType.Income &&
      (rt.transactions.length > 0 || rt.recurrence_type === Frequency.EveryYear),
  )

  return (
    <>
      {filteredTransactions.length !== 0 ? (
        <Stack
          sx={{
            height: forOverview ? 'calc(100vh - 62px)' : 'calc(100vh - 109px)',
            maxHeight: forOverview ? 'calc(100vh - 62px)' : 'calc(100vh - 109px)',
          }}
        >
          {forOverview && (
            <Text bold={true} sx={{ mb: 4, mt: 12, mx: 24 }} variant="body2">
              {copy.income}
            </Text>
          )}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List sx={{ bgcolor: 'background.paper' }}>
              {filteredTransactions.map((rt, index) => (
                <React.Fragment key={rt.guid}>
                  <RecurringRow onClick={onClick} repeatingTransaction={rt} />
                  <Divider
                    variant={index < filteredTransactions.length - 1 ? 'inset' : 'fullWidth'}
                  />
                </React.Fragment>
              ))}
            </List>
          </Box>
          <Box>
            <Divider />
            <Box
              sx={{
                alignContent: 'center',
                bgcolor: 'background.paper',
                justifyContent: 'flex-end',
                px: 24,
                py: 12,
                textAlign: 'right',
              }}
            >
              <AddRecurringTransaction
                isIncome={true}
                label={copy.add_income}
                onAddRecurringTransaction={onAdd}
              />
            </Box>
          </Box>
        </Stack>
      ) : (
        <RecurringSettingsZeroState zeroStateFor="income" />
      )}
    </>
  )
})
